import useProductsV2 from '@/api/inventory-management/products/useProductsV2';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Product } from '@/components/common/semantic-icons';
import { cn } from '@/lib/utils';
import { ProductV2DTO } from '@/types/application/inventory-management/ProductV2DTO';
import { useDebouncedState } from '@react-hookz/web';
import { useCallback, useState } from 'react';

function ProductV2Item({
  product,
  isTrigger = false,
}: {
  product: ProductV2DTO;
  isTrigger?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <Avatar className="size-6">
        <AvatarImage src={product.imageUrl ?? undefined} />
        <AvatarFallback>
          <Product />
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{product.name}</span>
        {!isTrigger && (
          <span className="text-xs text-muted-foreground">
            {product.baseSku || 'No SKU'}
          </span>
        )}
      </div>
    </div>
  );
}

export function ProductsV2Autocomplete({
  onProductSelect,
  selectedProductId,
  allowClear = false,
  className,
}: {
  onProductSelect: (productId?: string | undefined) => void;
  selectedProductId?: string;
  allowClear?: boolean;
  className?: string;
}) {
  const [productSearchQuery, _setProductSearchQuery] = useState('');
  const [apiSearchQuery, setApiSearchQuery] = useDebouncedState('', 150);

  const setProductSearchQuery = useCallback(
    (query: string) => {
      _setProductSearchQuery(query);
      setApiSearchQuery(query);
    },
    [setApiSearchQuery, _setProductSearchQuery],
  );

  const {
    data: productsData,
    isLoading: isLoadingProducts,
    error: productsLoadError,
  } = useProductsV2(
    {
      isSellable: true,
      page: 1,
      pageSize: 50,
      name: apiSearchQuery.length > 0 ? apiSearchQuery : undefined,
    },
    true,
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Autocomplete
        options={productsData?.edges ?? []}
        value={selectedProductId}
        disabled={isLoadingProducts}
        placeholder="Search for a product"
        getValue={(product) => product.id}
        getLabel={(product) => product.name}
        renderOption={(product) => <ProductV2Item product={product} />}
        renderTrigger={(product) => (
          <ProductV2Item product={product} isTrigger />
        )}
        searchValue={productSearchQuery}
        onSearchValueChange={setProductSearchQuery}
        onValueChange={onProductSelect}
        multiSelect={false}
        allowClear={allowClear}
        className={cn(productsLoadError && 'border-destructive')}
      />
    </div>
  );
}
